import { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import { favoritoDe, favoritoId } from '@/shared/domain/favoritos';
import type { OpcionConflicto } from '@/shared/domain/prefsConflicto';
import type {
  Favorito,
  PerfilTipo,
  PreferenciasConfig,
  PreferenciasServidor,
  SerieRef,
} from '@/shared/domain/types';
import { configParaPerfil } from '@/shared/domain/perfiles';
import { resolverConflictoPrefs } from '@/shared/store/listeners/prefsSync';
import type { AppStoreLike } from '@/shared/store/listeners/types';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import {
  patchConfig,
  replaceConfig,
  resetPrefs,
  toggleFavorito,
  type PrefsSyncStatus,
} from '@/shared/store/slices/prefsSlice';

export interface UsePrefs {
  config: PreferenciasConfig;
  customized: boolean;
  sync: PrefsSyncStatus;
  syncError: string | null;
  /** Copia de la cuenta mientras hay un conflicto por resolver. */
  conflict: PreferenciasServidor | null;
  patch: (cambios: Partial<PreferenciasConfig>) => void;
  /** Aplica la configuración recomendada de un perfil (reemplaza widgets e intereses). */
  aplicarPerfil: (perfil: PerfilTipo) => void;
  reset: () => void;
  resolveConflict: (opcion: OpcionConflicto) => void;
}

/** Preferencias del usuario (local primero; el listener las sincroniza con la cuenta). */
export function usePrefs(): UsePrefs {
  const dispatch = useAppDispatch();
  const store = useStore() as AppStoreLike;
  const prefs = useAppSelector((s) => s.prefs);
  return useMemo<UsePrefs>(
    () => ({
      config: prefs.config,
      customized: prefs.customized,
      sync: prefs.sync,
      syncError: prefs.syncError,
      conflict: prefs.conflict,
      patch: (cambios) => dispatch(patchConfig(cambios)),
      aplicarPerfil: (perfil) =>
        dispatch(
          replaceConfig({
            config: { ...configParaPerfil(perfil), favoritos: prefs.config.favoritos },
            customized: false,
          }),
        ),
      reset: () => dispatch(resetPrefs()),
      resolveConflict: (opcion) => resolverConflictoPrefs(store, opcion),
    }),
    [prefs, dispatch, store],
  );
}

export interface UseFavoritos {
  favoritos: Favorito[];
  esFavorito: (ref: SerieRef) => boolean;
  alternar: (ref: SerieRef) => void;
}

export function useFavoritos(): UseFavoritos {
  const dispatch = useAppDispatch();
  const favoritos = useAppSelector((s) => s.prefs.config.favoritos);
  const ids = useMemo(() => new Set(favoritos.map((f) => f.id)), [favoritos]);
  const esFavorito = useCallback((ref: SerieRef) => ids.has(favoritoId(ref)), [ids]);
  const alternar = useCallback(
    (ref: SerieRef) => dispatch(toggleFavorito(favoritoDe(ref, new Date().toISOString()))),
    [dispatch],
  );
  return { favoritos, esFavorito, alternar };
}
